import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Inbox } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "clb_admin_token";

const CONFIG_FIELDS = [
  ["phone", "Phone Number"],
  ["whatsapp", "WhatsApp Number (with country code, digits only)"],
  ["email", "Email"],
  ["address", "Address"],
  ["hours", "Business Hours"],
  ["mapsUrl", "Google Maps Embed URL"],
  ["instagram", "Instagram URL"],
  ["facebook", "Facebook URL"],
  ["logoUrl", "Official Logo Image URL"],
  ["whatsappMessage", "Default WhatsApp Message"],
];

const inputCls =
  "w-full border border-darknavy/20 bg-white px-4 py-3 text-sm text-darknavy outline-none transition-colors focus:border-aqua";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-darknavy px-5" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-cream p-10">
        <div className="flex h-12 w-12 items-center justify-center bg-navy">
          <span className="font-display text-sm font-bold tracking-widest text-cream">CLB</span>
        </div>
        <h1 className="mt-6 font-display text-2xl font-medium text-darknavy">Admin Login</h1>
        <p className="mt-1 text-sm text-darknavy/60">Chhannulal &amp; Brothers — enquiries &amp; site settings</p>
        <div className="mt-8 space-y-5">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" className={inputCls} data-testid="admin-email-input" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} data-testid="admin-password-input" />
        </div>
        <button type="submit" disabled={loading} data-testid="admin-login-button" className="mt-8 w-full bg-navy py-3.5 font-display text-sm font-semibold uppercase tracking-[0.15em] text-cream transition-colors hover:bg-aqua disabled:opacity-60">
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function Enquiries({ headers }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/enquiries`, { headers })
      .then((res) => setItems(res.data))
      .catch(() => toast.error("Could not load enquiries"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (loading) return <p className="py-16 text-center text-darknavy/50">Loading enquiries…</p>;
  if (items.length === 0)
    return (
      <div className="flex flex-col items-center py-16 text-center" data-testid="enquiries-empty">
        <Inbox className="h-10 w-10 text-darknavy/25" />
        <p className="mt-4 text-darknavy/60">No enquiries yet. New form submissions will appear here.</p>
      </div>
    );

  return (
    <div className="space-y-4" data-testid="enquiries-list">
      {items.map((e) => (
        <article key={e.id} className="border border-darknavy/10 bg-white p-6" data-testid={`enquiry-${e.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-lg font-medium text-darknavy">{e.name}</h3>
              <p className="text-sm text-darknavy/60">
                <a href={`tel:${e.phone}`} className="text-aqua hover:underline">{e.phone}</a>
                {e.email && <> · <a href={`mailto:${e.email}`} className="text-aqua hover:underline">{e.email}</a></>}
              </p>
            </div>
            <div className="text-right">
              {e.category && <span className="inline-block bg-navy px-3 py-1 font-display text-[10px] font-semibold uppercase tracking-[0.2em] text-cream">{e.category.replace(/-/g, " ")}</span>}
              <p className="mt-1 text-xs text-darknavy/40">{new Date(e.created_at).toLocaleString()}</p>
            </div>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-darknavy/75">{e.message}</p>
        </article>
      ))}
    </div>
  );
}

function Settings({ headers }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/config`).then((res) => setForm(res.data)).catch(() => toast.error("Could not load settings"));
  }, []);

  if (!form) return <p className="py-16 text-center text-darknavy/50">Loading settings…</p>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/config`, { updates: form }, { headers });
      setForm(data);
      toast.success("Settings saved. The website now uses these details.");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-2xl space-y-5" data-testid="settings-form">
      <p className="text-sm leading-relaxed text-darknavy/60">
        These values power the header, footer, contact page, WhatsApp button and enquiry messages across the entire website.
      </p>
      {CONFIG_FIELDS.map(([key, label]) => (
        <div key={key}>
          <label className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-navy" htmlFor={`cfg-${key}`}>{label}</label>
          <input id={`cfg-${key}`} data-testid={`settings-${key}`} value={form[key] || ""} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} className={`${inputCls} mt-1.5`} />
        </div>
      ))}
      <button type="submit" disabled={saving} data-testid="settings-save-button" className="bg-navy px-8 py-3.5 font-display text-sm font-semibold uppercase tracking-[0.15em] text-cream transition-colors hover:bg-aqua disabled:opacity-60">
        {saving ? "Saving…" : "Save Settings"}
      </button>
    </form>
  );
}

export default function Admin() {
  usePageMeta("Admin | Chhannulal & Brothers");
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("enquiries");

  if (!token) return <Login onLogin={setToken} />;

  const headers = { Authorization: `Bearer ${token}` };
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-darknavy/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center bg-navy font-display text-xs font-bold tracking-widest text-cream">CLB</span>
            <div>
              <h1 className="font-display text-lg font-medium text-darknavy">Admin Panel</h1>
              <p className="text-xs text-darknavy/50">Chhannulal &amp; Brothers</p>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 border border-darknavy/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-darknavy transition-colors hover:border-navy hover:text-navy">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-5 py-10">
        <div className="mb-8 flex gap-2">
          {[["enquiries", "Enquiries"], ["settings", "Site Settings"]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`px-5 py-2.5 font-display text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${tab === key ? "bg-navy text-cream" : "border border-darknavy/20 text-darknavy hover:border-navy"}`}
            >
              {label}
            </button>
          ))}
          {tab === "enquiries" && (
            <button onClick={() => setTab("enquiries") || window.location.reload()} className="ml-auto hidden" aria-hidden="true" />
          )}
        </div>
        {tab === "enquiries" ? <Enquiries headers={headers} /> : <Settings headers={headers} />}
      </div>
    </div>
  );
}
